// src/pages/AdminFormsPage.jsx
import { useEffect, useState } from 'react';
import api from '../api/api';
import TopNav from '../components/TopNav';
import useBusinessAddress from '../hooks/useBusinessAddress';
import { businessAddressTemplateValues } from '../utils/businessAddress';

export default function AdminFormsPage() {
  const businessAddress = useBusinessAddress();
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [placeholders, setPlaceholders] = useState(['name','employer','weekEnding','days','hours','empType']);

  // Include common vars + footer vars so preview looks nice
  const sampleVars = {
    name: 'Jane Doe',
    employer: 'Company LLC',
    weekEnding: '09/04/2025',
    days: 5,
    hours: 40,
    empType: 'Full Time',
    // footer-specific demo values
    ...businessAddressTemplateValues(businessAddress || {}),
    brand: 'IRENES',
    brand_sub: 'NYC',
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get('/forms');
      setForms(data);
    })();
  }, []);

  const pick = (f) => {
    setSelected(f);
    setContent(f.content || '');
    setTitle(f.title || '');
    setDesc(f.description || '');
    setPlaceholders(Array.isArray(f.placeholders) ? f.placeholders : placeholders);
  };

  // NO placeholders for header in admin preview
function renderHeaderPreview(str) {
  const lines = String(str || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);

  const line1 = lines[0] || '';
  const line2 = lines[1] || '';

  return (
    <div className="print-head">
      <div className="brand-block">
        <strong>{line1}</strong>
        {line2 ? <span>{line2}</span> : null}
      </div>
    </div>
  );
}

  // ---- unified preview renderer with footer mode ----
  const renderPreview = (str, { isFooter = false } = {}) => {
    let replaced = (str || '').replace(/\{(\w+)\}/g, (_, k) => (sampleVars[k] ?? ' '));
    replaced = replaced.replace(/\s+([.,;:!?])/g, '$1'); // trim space before punctuation

    const chunks = isFooter
      ? replaced.split(/\n/)           // footer: one <p> per line
      : replaced.split(/\n\s*\n/);     // others: paragraphs by blank line

    const renderInline = (text) =>
      text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        /^\*\*[^*]+\*\*$/.test(part)
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : <span key={i}>{part}</span>
      );

    return chunks
      .map(p => p.trim())
      .filter(Boolean)
      .map((p, i) => <p key={i}>{renderInline(p)}</p>);
  };

  const save = async () => {
    if (!selected) return;
    await api.patch(`/forms/${selected.id}`, {
      title, description: desc, content, placeholders
    });
    const { data } = await api.get('/forms');
    setForms(data);
    const updated = data.find(x => x.id === selected.id);
    pick(updated);
  };

  return (
    <>
      <TopNav />
      <div className="inventory-container">
        <h2>Admin: Templates</h2>
        <div className="card-list" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 12 }}>
          <div className="card">
            <h3>Templates</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {forms.map(f => (
                <li key={f.id}>
                  <button
                    className={`button small ${selected?.id===f.id?'blue':''}`}
                    style={{ width: '100%', textAlign: 'left', marginBottom: 6 }}
                    onClick={() => pick(f)}
                  >
                    {f.title}
                    <div style={{ fontSize: 12, color:'#666' }}>{f.key}</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="card" style={{ minHeight: 400 }}>
            {selected ? (
              <>
                <div className="form-group">
                  <label>Title</label>
                  <input className="input" value={title} onChange={e=>setTitle(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input className="input" value={desc} onChange={e=>setDesc(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    className="input"
                    rows={12}
                    value={content}
                    onChange={e=>setContent(e.target.value)}
                  />
                  <div style={{ fontSize:12, color:'#555', marginTop:4 }}>
                    Use placeholders: {'{name} {employer} {weekEnding} {days} {hours} {empType} {company} {address_line} {city} {state} {zip} {phone} {brand} {brand_sub}'}.
                    Use **bold** with double asterisks. Blank line = new paragraph (except footer, which is one line per paragraph).
                  </div>
                </div>
                <div className="form-group">
                  <label>Allowed Placeholders</label>
                  <input
                    className="input"
                    value={placeholders.join(',')}
                    onChange={e=>setPlaceholders(
                      e.target.value.split(',').map(s=>s.trim()).filter(Boolean)
                    )}
                  />
                </div>

                <div style={{ display:'flex', gap:12 }}>
                  <button className="button small blue" onClick={save}>Save</button>
                </div>

                <hr />
                <h3>Preview</h3>
                <div className="preview">
                    {selected?.key === 'timesheet_header' 
                        ? renderHeaderPreview(content)     // ← no placeholders for header
                        : renderPreview(content, { isFooter: selected?.key === 'timesheet_footer' })           // ← existing preview with placeholders for other forms
                    }
                </div>
              </>
            ) : (
              <em>Select a template…</em>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
